import { EMPTY_ARRAY } from "@faro-lotv/foundation";
import {
  isDeletedRevisionStatus,
  RegistrationEdgeRevision,
  RegistrationEdgeType,
} from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import { areConnectionsEqual } from "../utils/edge-utils";
import {
  selectHoveredConnectionDetails,
  selectHoveredEntityIdsRecursive,
  selectPartOfConnectedComponents,
  selectValidRevisionScans,
  selectVisibilityMap,
} from "./data-preparation-ui/data-preparation-ui-selectors";
import {
  selectScanMarkersVisibilityMode,
  selectShowConnectionLines,
  selectShowDiscardedConnectionLines,
} from "./data-preparation-view-options/data-preparation-view-options-selectors";
import { ScanMarkersVisibilityMode } from "./data-preparation-view-options/data-preparation-view-options-slice";
import {
  selectPartOfEdges,
  selectRegistrationEdgeType,
  selectRevisionRegistrationEdges,
} from "./revision/revision-edge-selectors";
import { selectUpdatedRevisionEdges } from "./user-edits/data-preparation-user-edit-selectors";

/**
 * @returns the registration edges to display to the user.
 * Considers user edits and edge display settings.
 */
export const selectActiveRevisionEdges = createSelector(
  [
    selectShowConnectionLines,
    selectShowDiscardedConnectionLines,
    selectRevisionRegistrationEdges,
    selectUpdatedRevisionEdges,
    selectRegistrationEdgeType,
    selectScanMarkersVisibilityMode,
    selectVisibilityMap,
    selectValidRevisionScans,
  ],
  (
    showConnectionLines,
    showDiscardedConnectionLines,
    revisionEdges,
    updatedEdges,
    edgeType,
    visibilityMode,
    visibilityMap,
    validScans,
  ): RegistrationEdgeRevision[] => {
    if (
      !showConnectionLines ||
      visibilityMode === ScanMarkersVisibilityMode.showNone
    ) {
      return EMPTY_ARRAY;
    }

    const validEdges = revisionEdges.filter((edge) => {
      const deleted = isDeletedRevisionStatus(edge.status);
      // When dev option is enabled, still show local & global edges that were deleted
      // Allows debugging of registration issues
      const showDiscarded =
        showDiscardedConnectionLines &&
        (edge.type === RegistrationEdgeType.local ||
          RegistrationEdgeType.global);

      return !deleted || showDiscarded;
    });

    // Remove deleted or invalidated edges
    const edgesAfterEdits = validEdges.filter(
      (edge) => !updatedEdges.deletedEdges.has(edge.id),
    );

    // Add new edges
    edgesAfterEdits.push(
      // Mock edges from the user edits
      // All important properties are present, but TS cannot infer that these are valid edges
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      ...(updatedEdges.newEdges as RegistrationEdgeRevision[]),
    );

    const candidateEdges = edgesAfterEdits.filter(
      (edge) => edge.type === RegistrationEdgeType.candidate,
    );

    // Filter by the currently selected edge type
    const edgesByType = edgesAfterEdits.filter(
      (edge) => edge.type === edgeType,
    );

    const activeEdges = [...edgesByType];

    // Add candidate edges that don't have a matching active edge
    for (const candidate of candidateEdges) {
      if (
        !edgesByType.some((active) => areConnectionsEqual(candidate, active))
      ) {
        activeEdges.push(candidate);
      }
    }

    if (visibilityMode === ScanMarkersVisibilityMode.showAll) {
      return activeEdges;
    }

    const validScanIds = new Set(validScans.map((scan) => scan.id));

    // Filter out edges connected to invisible entities and deleted scans
    return activeEdges.filter((edge) => {
      const connectedToInvisible =
        visibilityMap[edge.sourceId] === false ||
        visibilityMap[edge.targetId] === false;

      const connectedToDeletedScans =
        !validScanIds.has(edge.sourceId) || !validScanIds.has(edge.targetId);

      return !connectedToInvisible && !connectedToDeletedScans;
    });
  },
);

/**
 * @returns All loaded global registration edges in the revision which are relevant
 * for the current revision.
 */
export const selectRelevantGlobalEdges = createSelector(
  [selectRevisionRegistrationEdges, selectUpdatedRevisionEdges],
  (edges, updatedEdges) => {
    const edgesAfterEdits = edges.filter(
      (edge) =>
        edge.type === RegistrationEdgeType.global &&
        !isDeletedRevisionStatus(edge.status) &&
        !updatedEdges.deletedEdges.has(edge.id),
    );

    // Add new edges
    const newGlobalEdges = updatedEdges.newEdges.filter(
      (
        edge,
      ): edge is RegistrationEdgeRevision & {
        type: RegistrationEdgeType.global;
      } => edge.type === RegistrationEdgeType.global,
    );

    edgesAfterEdits.push(...newGlobalEdges);

    return edgesAfterEdits;
  },
);

/**
 * @returns the hovered registration edge array to display to the user.
 * Considers user edits and edge display settings.
 */
export const selectActiveRevisionHoveredEdges = createSelector(
  [selectActiveRevisionEdges, selectHoveredConnectionDetails],
  (
    activeRegistrationEdges,
    hoveredRegistrationEdge,
  ): RegistrationEdgeRevision[] =>
    hoveredRegistrationEdge
      ? activeRegistrationEdges.filter(
          (edge) => edge.id === hoveredRegistrationEdge.id,
        )
      : EMPTY_ARRAY,
);

/**
 * @returns the PartOf edges that should be shown the user.
 * Filters out invalid edges and only includes edges connected to hovered entities, to not clutter the view.
 */
export const selectActivePartOfEdges = createSelector(
  [
    selectPartOfEdges,
    selectPartOfConnectedComponents,
    selectUpdatedRevisionEdges,
    selectHoveredEntityIdsRecursive,
    selectScanMarkersVisibilityMode,
    selectVisibilityMap,
  ],
  (
    partOfEdges,
    partOfComponents,
    updatedEdges,
    hoveredEntityIds,
    scanMarkersVisibilityMode,
    visibilityMap,
  ) => {
    if (scanMarkersVisibilityMode === ScanMarkersVisibilityMode.showNone) {
      return EMPTY_ARRAY;
    }

    return partOfEdges.filter((edge) => {
      const edgeComponent = partOfComponents.find((component) =>
        component.includes(edge.sourceId),
      );

      // Don't show edges where either connected scan is hidden
      if (
        scanMarkersVisibilityMode === ScanMarkersVisibilityMode.showVisible &&
        (visibilityMap[edge.sourceId] === false ||
          visibilityMap[edge.targetId] === false)
      ) {
        return false;
      }

      return (
        // Don't show edges deleted on the revision
        !isDeletedRevisionStatus(edge.status) &&
        // Don't show edges that were deleted by user edits
        !updatedEdges.deletedEdges.has(edge.id) &&
        // Only show PartOf edges on hover, to not clutter the view
        !!hoveredEntityIds &&
        hoveredEntityIds.some((hoveredId) => edgeComponent?.includes(hoveredId))
      );
    });
  },
);
